import { Injectable, inject, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, take, map, combineLatest } from 'rxjs';

import { AppState } from '../app.state';
import { ProductV2, ProductV2Variation } from '../../models/product-v2.model';
import {
  isBlipGoCartItem,
  isBlipGoProduct,
  isBlipGoVariationUuid,
} from '../../constants/blip-go.constants';
import { BlipGoContractService } from '../../services/blip-go-contract.service';
import { ToastService } from '../../../shared/services/toast.service';
import { blockCartHydration } from '../../utils/cart-hydration.util';

import { CartState, CartItem, generateCartItemId } from './cart.state';
import { CartPersistenceService } from './cart-persistence.service';
import { CartSyncService } from './cart-sync.service';
import { CartValidationService } from './cart-validation.service';
import { CartCacheService } from './cart-cache.service';
import { CartErrorHandlerService } from './cart-error-handler.service';
import {
  selectCartState,
  selectCartItems,
  selectCartItemCount,
  selectCartTotal,
  selectCartDisplayedTotal,
  CartDisplayedTotal,
  selectCartSubtotal,
  selectCartDiscount,
  selectCartLoading,
  selectUrlCartLoading,
  selectCartError,
  selectCartLastUpdated,
  selectIsCartEmpty,
  selectCartItemById,
  selectCartItemQuantity,
  selectCartHeaderWithCount,
  selectCartSidebarOpen,
  selectCartSidebarHeader,
  selectSyncInProgress,
  selectLastSync,
  selectSyncError,
  selectSyncRetryCount,
  selectCartValidationStatus,
  selectCartStats
} from './cart.selectors';
import * as CartActions from './cart.actions';

@Injectable({
  providedIn: 'root',
})
export class CartFacade {
  private store = inject(Store<AppState>);
  private cartPersistenceService = inject(CartPersistenceService);
  private cartSyncService = inject(CartSyncService);
  private validationService = inject(CartValidationService);
  private cacheService = inject(CartCacheService);
  private errorHandler = inject(CartErrorHandlerService);
  private toastService = inject(ToastService);
  private injector = inject(Injector);

  private readonly BLIP_GO_SINGLE_PLAN_TOAST =
    'Você só pode adicionar 1 plano Blip Go ao carrinho.';

  // ===== STATE SELECTORS =====
  
  getCartState(): Observable<CartState> {
    return this.store.select(selectCartState);
  }

  getCartItems(): Observable<CartItem[]> {
    return this.store.select(selectCartItems);
  }

  getCartItemCount(): Observable<number> {
    return this.store.select(selectCartItemCount);
  }

  getCartTotal(): Observable<number> {
    return this.store.select(selectCartTotal);
  }

  getCartDisplayedTotal(): Observable<CartDisplayedTotal> {
    return this.store.select(selectCartDisplayedTotal);
  }

  getCartSubtotal(): Observable<number> {
    return this.store.select(selectCartSubtotal);
  }

  getCartDiscount(): Observable<number> {
    return this.store.select(selectCartDiscount);
  }

  getIsLoading(): Observable<boolean> {
    return this.store.select(selectCartLoading);
  }

  getUrlCartLoading(): Observable<boolean> {
    return this.store.select(selectUrlCartLoading);
  }

  getError(): Observable<string | null> {
    return this.store.select(selectCartError);
  }

  getLastUpdated(): Observable<number | null> {
    return this.store.select(selectCartLastUpdated);
  }

  getIsCartEmpty(): Observable<boolean> {
    return this.store.select(selectIsCartEmpty);
  }

  // ===== ITEM SELECTORS =====

  getCartItemById(itemId: string): Observable<CartItem | undefined> {
    return this.store.select(selectCartItemById(itemId));
  }

  getCartItemQuantity(itemId: string): Observable<number> {
    return this.store.select(selectCartItemQuantity(itemId));
  }

  // ===== UI SELECTORS =====

  getCartSidebarOpen(): Observable<boolean> {
    return this.store.select(selectCartSidebarOpen);
  }

  getCartSidebarHeader(): Observable<{ icon?: string; customIcon?: unknown; title: string }> {
    return this.store.select(selectCartSidebarHeader);
  }

  getCartHeaderWithCount(): Observable<any> {
    return this.store.select(selectCartHeaderWithCount);
  }

  // ===== SYNC SELECTORS =====

  getSyncInProgress(): Observable<boolean> {
    return this.store.select(selectSyncInProgress);
  }

  getLastSync(): Observable<number | null> {
    return this.store.select(selectLastSync);
  }

  getSyncError(): Observable<string | null> {
    return this.store.select(selectSyncError);
  }

  getSyncRetryCount(): Observable<number> {
    return this.store.select(selectSyncRetryCount);
  }

  // ===== VALIDATION SELECTORS =====

  getCartValidationStatus(): Observable<{
    totalItems: number;
    itemsWithVariations: number;
    itemsWithoutPrice: number;
    isValid: boolean;
    warnings: { key: string; params?: any }[];
  }> {
    return this.store.select(selectCartValidationStatus);
  }

  // ===== STATS SELECTORS =====

  getCartStats(): Observable<{
    itemCount: number;
    totalQuantity: number;
    totalValue: number;
    lastUpdated: number | null;
    averageItemValue: number;
  }> {
    return this.store.select(selectCartStats);
  }

  // ===== CART ACTIONS =====

  private hasMindCategoryInCart(product: ProductV2): boolean {
    let exists = false;

    this.getCartItems().pipe(take(1)).subscribe(items => {
      exists = items.some(i =>
        i.product?.productType === 'MIND' &&
        i.product?.sku === product.sku
      );
    });

    return exists;
  }

  private hasBlipGoInCart(): boolean {
    let exists = false;

    this.getCartItems().pipe(take(1)).subscribe(items => {
      exists = items.some(item => isBlipGoCartItem(item));
    });

    return exists;
  }

  private isBlipGoAddRequest(
    product: ProductV2,
    selectedVariation?: ProductV2Variation
  ): boolean {
    return isBlipGoProduct(product) || isBlipGoVariationUuid(selectedVariation?.uuid);
  }

  addToCart(
    product: ProductV2,
    quantity: number,
    selectedVariation?: ProductV2Variation,
    options?: { suppressSuccessToast?: boolean }
  ): boolean {
    const blipGoContractService = this.injector.get(BlipGoContractService);
    const isBlipGo = this.isBlipGoAddRequest(product, selectedVariation);

    if (blipGoContractService.shouldHideBlipGo() && isBlipGo) {
      console.warn('Blip Go já contratado — produto não pode ser adicionado ao carrinho');
      return false;
    }

    if (isBlipGo && this.hasBlipGoInCart()) {
      this.toastService.showWarning(this.BLIP_GO_SINGLE_PLAN_TOAST, 5000, 'top-center');
      return false;
    }

    if (isBlipGo) {
      quantity = 1;
    }
      
    if (product.productType === 'MIND' && product.sku) {
      if (this.hasMindCategoryInCart(product)) {
          console.warn(`Já existe produto da categoria ${product.sku} no carrinho`);
          return false;
      }
    }

    try {
      const validationResult = this.validationService.validateProductForCart(
        product, 
        quantity, 
        selectedVariation
      );
      
      if (!validationResult.isValid) {
        this.errorHandler.handleValidationError(
          validationResult.errors.join(', '),
          'Add to Cart'
        );
        return false;
      }

      this.cacheService.cacheProduct(product);
      if (selectedVariation) {
        this.cacheService.cacheVariation(selectedVariation);
      }

      this.store.dispatch(
        CartActions.addItem({
          product,
          quantity,
          selectedVariation,
          suppressSuccessToast: options?.suppressSuccessToast,
        })
      );

      return true;
    } catch (error) {
      this.errorHandler.handleError(error, 'Add to Cart');
      return false;
    }
  }


  removeFromCart(itemId: string): void {
    try {
      let removedItem: CartItem | undefined;
      this.getCartItemById(itemId)
        .pipe(take(1))
        .subscribe(item => {
          removedItem = item;
        });

      this.cacheService.removeCachedCartItem(itemId);
      this.store.dispatch(CartActions.removeItem({ itemId, removedItem }));
    } catch (error) {
      this.errorHandler.handleError(error, 'Remove from Cart');
    }
  }


  /**
   * Update item quantity
   */
  updateCartItemQuantity(itemId: string, quantity: number): boolean {
    try {
      if (quantity <= 0) {
        this.errorHandler.handleValidationError(
          'Quantidade deve ser maior que zero',
          'Update Quantity'
        );
        return false;
      }

      let blipGoBlocked = false;
      this.getCartItemById(itemId).pipe(take(1)).subscribe(item => {
        if (item && isBlipGoCartItem(item) && quantity > 1) {
          blipGoBlocked = true;
        }
      });

      if (blipGoBlocked) {
        this.toastService.showWarning(this.BLIP_GO_SINGLE_PLAN_TOAST, 5000, 'top-center');
        return false;
      }

      this.getCartItemById(itemId).pipe(take(1)).subscribe(item => {
        if (item) {
          const updatedItem = { ...item, quantity, lastModified: Date.now() };
          this.cacheService.cacheCartItem(updatedItem);
        }
      });

      this.store.dispatch(CartActions.updateQuantity({ itemId, quantity }));
      return true;
    } catch (error) {
      this.errorHandler.handleError(error, 'Update Quantity');
      return false;
    }
  }

  updateCartItemProduct(itemId: string, product: ProductV2): boolean {
    try {
      const validationResult = this.validationService.validateProductForCart(
        product, 
        1
      );
      
      if (!validationResult.isValid) {
        this.errorHandler.handleValidationError(
          validationResult.errors.join(', '),
          'Update Product'
        );
        return false;
      }

      this.cacheService.cacheProduct(product);

      this.store.dispatch(CartActions.updateProduct({ itemId, product }));
      return true;
    } catch (error) {
      this.errorHandler.handleError(error, 'Update Product');
      return false;
    }
  }

  updateCartItem(itemId: string, updates: Partial<CartItem>): boolean {
    try {
      this.store.dispatch(CartActions.updateCartItem({ itemId, updates }));
      return true;
    } catch (error) {
      this.errorHandler.handleError(error, 'Update Cart Item');
      return false;
    }
  }

  updateCartItemLinkedMsisdns(itemId: string, linkedMsisdns: string[]): boolean {
    try {
      this.store.dispatch(CartActions.updateCartItem({ 
        itemId, 
        updates: { linkedMsisdns, lastModified: Date.now() } 
      }));
      return true;
    } catch (error) {
      this.errorHandler.handleError(error, 'Update Cart Item Linked MSISDNs');
      return false;
    }
  }

  clearCart(options?: { emitEvent?: boolean }): void {
    try {
      let clearedItems: CartItem[] = [];
      this.getCartItems()
        .pipe(take(1))
        .subscribe(items => {
          clearedItems = items ?? [];
        });

      this.cacheService.clearAllCaches();
      this.store.dispatch(
        CartActions.clearCart({
          clearedItems,
          emitEvent: options?.emitEvent === true,
        })
      );
    } catch (error) {
      this.errorHandler.handleError(error, 'Clear Cart');
    }
  }

  clearExternalChannelCart(): void {
    try {
      blockCartHydration();
      this.cartPersistenceService.clearCartFromLocalStorage();
      this.cacheService.clearAllCaches();
      this.store.dispatch(CartActions.clearLocalCart());
    } catch (error) {
      this.errorHandler.handleError(error, 'Clear External Channel Cart');
    }
  }

  loadCart(): void {
    try {
      this.store.dispatch(CartActions.loadCart());
    } catch (error) {
      this.errorHandler.handleError(error, 'Load Cart');
    }
  }

  syncCart(): void {
    try {
      this.store.dispatch(CartActions.syncCart());
    } catch (error) {
      this.errorHandler.handleError(error, 'Sync Cart');
    }
  }

  // ===== UI ACTIONS =====

  setLoading(loading: boolean): void {
    this.store.dispatch(CartActions.setLoading({ loading }));
  }

  setUrlCartLoading(loading: boolean): void {
    this.store.dispatch(CartActions.setUrlCartLoading({ loading }));
  }

  openCartSidebar(): void {
    this.store.dispatch(CartActions.openCartSidebar());
  }

  closeCartSidebar(): void {
    this.store.dispatch(CartActions.closeCartSidebar());
  }

  setSidebarHeader(icon?: string, customIcon?: unknown, title?: string): void {
    this.store.dispatch(CartActions.setSidebarHeader({ 
      icon: icon || '', 
      customIcon, 
      title: title || 'Carrinho' 
    }));
  }

  // ===== PERSISTENCE METHODS =====

  saveCartToLocalStorage(): void {
    this.cartPersistenceService.saveCartToLocalStorage();
  }

  saveCartToLocalStorageDirect(items: CartItem[]): void {
    this.cartPersistenceService.saveCartToLocalStorageDirect(items);
  }

  loadCartFromLocalStorage(): CartItem[] {
    return this.cartPersistenceService.loadCartFromLocalStorage();
  }

  clearCartFromLocalStorage(): void {
    this.cartPersistenceService.clearCartFromLocalStorage();
  }

  hasStoredCart(): boolean {
    return this.cartPersistenceService.hasStoredCart();
  }

  getStoredCartItemCount(): number {
    return this.cartPersistenceService.getStoredCartItemCount();
  }

  createBackup(): void {
    this.getCartItems().pipe(take(1)).subscribe(items => {
      this.cartPersistenceService.createBackup(items);
    });
  }

  restoreFromBackup(): CartItem[] {
    return this.cartPersistenceService.restoreFromBackup();
  }

  // ===== SYNC METHODS =====

  syncCartToBackend(): void {
    this.cartSyncService.syncCartToBackend();
  }

  clearCartFromBackend(): void {
    this.cartSyncService.clearCartFromBackend();
  }

  forceSync(): void {
    this.cartSyncService.forceSync();
  }

  syncCartImmediately(): void {
    this.cartSyncService.syncCartToBackend();
  }

  syncOnPageUnload(): void {
    this.cartSyncService.syncOnPageUnload();
  }

  isSyncNeeded(): boolean {
    return this.cartSyncService.isSyncNeeded();
  }

  // ===== VALIDATION METHODS =====

  validateCartForCheckout(): boolean {
    let isValid = false;
    this.getCartItems().pipe(take(1)).subscribe(items => {
      const result = this.validationService.validateCartForCheckout(items);
      isValid = result.isValid;
      
      if (!result.isValid) {
        this.errorHandler.handleValidationError(
          result.errors.join(', '),
          'Checkout Validation'
        );
      }
    });
    
    return isValid;
  }

  getValidationSummary(): {
    totalItems: number;
    validItems: number;
    invalidItems: number;
    warningCount: number;
    errors: string[];
    warnings: string[];
  } | null {
    let summary: ReturnType<CartValidationService['getValidationSummary']> | null = null;
    this.getCartItems().pipe(take(1)).subscribe(items => {
      summary = this.validationService.getValidationSummary(items);
    });
    return summary;
  }

  // ===== CACHE METHODS =====

  warmUpCache(): void {
    this.getCartItems().pipe(take(1)).subscribe(items => {
      this.cacheService.warmUpCache(items);
    });
  }

  getCacheStats(): {
    hits: number;
    misses: number;
    size: number;
    hitRate: number;
  } {
    return this.cacheService.getCacheStats();
  }

  clearAllCaches(): void {
    this.cacheService.clearAllCaches();
  }

  // ===== ERROR HANDLING METHODS =====

  getErrorHistory(): {
    type: string;
    message: string;
    context?: string;
    originalError?: unknown;
    timestamp: number;
    retryable: boolean;
  }[] {
    return this.errorHandler.getErrorHistory();
  }

  getErrorStats(): {
    total: number;
    byType: Record<string, number>;
    retryable: number;
    recent: number;
  } {
    return this.errorHandler.getErrorStats();
  }

  clearErrorHistory(): void {
    this.errorHandler.clearErrorHistory();
  }

  // ===== UTILITY METHODS =====

  getItemId(product: ProductV2, selectedVariation?: ProductV2Variation): string {
    return generateCartItemId(product, selectedVariation);
  }

  hasItemInCart(product: ProductV2, selectedVariation?: ProductV2Variation): Observable<boolean> {
    const itemId = this.getItemId(product, selectedVariation);   
    return this.getCartItems().pipe(
      map(items => {
        const exists = items.some(item => item.id === itemId);
        return exists;
      })
    );
  }

  getItemQuantityInCart(product: ProductV2, selectedVariation?: ProductV2Variation): Observable<number> {
    const itemId = this.getItemId(product, selectedVariation);
    return this.getCartItems().pipe(
      map(items => {
        const item = items.find(item => item.id === itemId);
        const quantity = item?.quantity || 0;
        return quantity;
      })
    );
  }

 getCartSummary(): Observable<{
    items: CartItem[];
    count: number;
    total: number;
    subtotal: number;
    discount: number;
    validation: {
      totalItems: number;
      itemsWithVariations: number;
      itemsWithoutPrice: number;
      isValid: boolean;
      warnings: { key: string; params?: any }[];
    };
    sync: {
      inProgress: boolean;
      error: string | null;
    };
    cache: {
      hits: number;
      misses: number;
      size: number;
      hitRate: number;
    };
    errors: {
      total: number;
      byType: Record<string, number>;
      retryable: number;
      recent: number;
    };
  }> {
    return combineLatest([
      this.getCartItems(),
      this.getCartItemCount(),
      this.getCartTotal(),
      this.getCartSubtotal(),
      this.getCartDiscount(),
      this.getCartValidationStatus(),
      this.getSyncInProgress(),
      this.getError()
    ]).pipe(
      map(([items, count, total, subtotal, discount, validation, syncInProgress, error]) => ({
        items,
        count,
        total,
        subtotal,
        discount,
        validation,
        sync: {
          inProgress: syncInProgress,
          error
        },
        cache: this.getCacheStats(),
        errors: this.getErrorStats()
      }))
    );
  }
}
